import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowForwardRounded,
  GavelRounded,
  InsightsRounded,
  LibraryAddRounded,
  RestartAltRounded,
  SearchRounded,
  VerifiedUserOutlined,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from 'recharts';

const API_BASE = process.env.REACT_APP_API_BASE || '';
const formatNumber = value => new Intl.NumberFormat('en-CA').format(Number(value || 0));

function StatCard({ label, value, detail, tone = '#14877F' }) {
  return (
    <Paper variant="outlined" sx={{ p: 2.25, borderRadius: 3, minWidth: 0, position: 'relative', overflow: 'hidden' }}>
      <Box sx={{ position: 'absolute', inset: '0 auto 0 0', width: 4, bgcolor: tone }} />
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.055em' }}>{label}</Typography>
      <Typography sx={{ mt: 0.45, fontSize: { xs: 24, xl: 29 }, fontWeight: 820, letterSpacing: '-.035em' }}>{formatNumber(value)}</Typography>
      <Typography variant="caption" color="text.secondary">{detail}</Typography>
    </Paper>
  );
}

function HorizontalBars({ title, subtitle, data, color, onOpen }) {
  return (
    <Paper variant="outlined" sx={{ p: 2.25, borderRadius: 3, minWidth: 0 }}>
      <Typography sx={{ fontWeight: 800 }}>{title}</Typography>
      <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
      <Box sx={{ height: 330, mt: 1.5 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data.slice(0, 10)} layout="vertical" margin={{ top: 4, right: 24, bottom: 4, left: 18 }}>
            <CartesianGrid strokeDasharray="3 3" horizontal={false} opacity={0.35} />
            <XAxis type="number" tickFormatter={formatNumber} tick={{ fontSize: 11 }} />
            <YAxis type="category" dataKey="label" width={150} tick={{ fontSize: 11 }} interval={0} />
            <ChartTooltip formatter={value => [formatNumber(value), 'Cases']} />
            <Bar dataKey="cases" fill={color} radius={[0, 5, 5, 0]} onClick={item => onOpen?.(item.payload || item)} cursor={onOpen ? 'pointer' : 'default'} />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Paper>
  );
}

export default function CaseResearchAnalytics({ embedded = false }) {
  const navigate = useNavigate();
  const [initialParams] = useState(() => new URLSearchParams(window.location.search));
  const [status, setStatus] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [court, setCourt] = useState(initialParams.get('court') || '');
  const [yearFrom, setYearFrom] = useState(initialParams.get('yearFrom') || '');
  const [yearTo, setYearTo] = useState(initialParams.get('yearTo') || '');
  const [query, setQuery] = useState(initialParams.get('q') || '');
  const [draftQuery, setDraftQuery] = useState(initialParams.get('q') || '');
  const [starterCases, setStarterCases] = useState([]);
  const [starterTotal, setStarterTotal] = useState(0);
  const [starterLoading, setStarterLoading] = useState(false);
  const [starterError, setStarterError] = useState('');

  useEffect(() => {
    const controller = new AbortController();
    fetch(`${API_BASE}/api/archive-cases/status`, { signal: controller.signal })
      .then(response => response.ok ? response.json() : Promise.reject(new Error('Unable to read archive status.')))
      .then(setStatus)
      .catch(requestError => { if (requestError.name !== 'AbortError') setError(requestError.message); });
    return () => controller.abort();
  }, []);

  const load = useCallback(() => {
    if (!status?.available) {
      setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    const params = new URLSearchParams();
    if (court) params.set('court', court);
    if (yearFrom) params.set('yearFrom', yearFrom);
    if (yearTo) params.set('yearTo', yearTo);
    setLoading(true);
    setError('');
    fetch(`${API_BASE}/api/archive-cases/analytics/overview?${params}`, { signal: controller.signal })
      .then(response => response.ok ? response.json() : response.json().then(body => Promise.reject(new Error(body.error || 'Unable to load case research patterns.'))))
      .then(setData)
      .catch(requestError => { if (requestError.name !== 'AbortError') setError(requestError.message); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [status, court, yearFrom, yearTo]);

  useEffect(() => {
    let cancelRequest = () => {};
    const timer = window.setTimeout(() => { cancelRequest = load() || (() => {}); }, 300);
    return () => { window.clearTimeout(timer); cancelRequest(); };
  }, [load]);

  const loadStarterCases = useCallback(() => {
    if (!status?.available) return undefined;
    const controller = new AbortController();
    const params = new URLSearchParams({ page: '1', pageSize: '10', sort: query ? 'relevance' : 'newest' });
    if (query) params.set('q', query);
    if (court) params.set('court', court);
    if (yearFrom) params.set('yearFrom', yearFrom);
    if (yearTo) params.set('yearTo', yearTo);
    setStarterLoading(true);
    setStarterError('');
    fetch(`${API_BASE}/api/archive-cases?${params.toString()}`, { signal: controller.signal })
      .then(response => response.ok ? response.json() : response.json().then(body => Promise.reject(new Error(body.error || 'Unable to load decisions.'))))
      .then(result => {
        setStarterCases(Array.isArray(result?.cases) ? result.cases.slice(0, 6) : []);
        setStarterTotal(Number(result?.total || 0));
      })
      .catch(requestError => { if (requestError.name !== 'AbortError') setStarterError(requestError.message); })
      .finally(() => { if (!controller.signal.aborted) setStarterLoading(false); });
    return () => controller.abort();
  }, [status, query, court, yearFrom, yearTo]);

  useEffect(() => {
    let cancelRequest = () => {};
    const timer = window.setTimeout(() => { cancelRequest = loadStarterCases() || (() => {}); }, 0);
    return () => { window.clearTimeout(timer); cancelRequest(); };
  }, [loadStarterCases]);

  const openArchive = useCallback(filters => {
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    if (court) params.set('court', court);
    if (yearFrom) params.set('yearFrom', yearFrom);
    if (yearTo) params.set('yearTo', yearTo);
    Object.entries(filters || {}).forEach(([key, value]) => { if (value) params.set(key, value); });
    params.set('view', 'decisions');
    navigate(`/research?${params.toString()}`);
  }, [court, navigate, query, yearFrom, yearTo]);

  const searchDecisions = event => {
    event?.preventDefault();
    const nextQuery = draftQuery.trim();
    setQuery(nextQuery);
    const params = new URLSearchParams();
    params.set('view', 'decisions');
    if (nextQuery) params.set('q', nextQuery);
    if (court) params.set('court', court);
    if (yearFrom) params.set('yearFrom', yearFrom);
    if (yearTo) params.set('yearTo', yearTo);
    navigate(`/research?${params.toString()}`);
  };

  const openDecision = decision => {
    const params = new URLSearchParams();
    params.set('view', 'decisions');
    if (query) params.set('q', query);
    if (court) params.set('court', court);
    if (yearFrom) params.set('yearFrom', yearFrom);
    if (yearTo) params.set('yearTo', yearTo);
    params.set('case', decision.id);
    if (decision.name) params.set('title', decision.name);
    if (decision.citation) params.set('citation', decision.citation);
    navigate(`/research?${params.toString()}`);
  };

  const showNewestDecisions = () => {
    setDraftQuery('');
    setQuery('');
    const params = new URLSearchParams(window.location.search);
    params.set('view', 'patterns');
    params.delete('q');
    navigate(`/research?${params.toString()}`, { replace: true });
  };

  const trendData = useMemo(() => {
    const rows = data?.trends || [];
    const latest = Math.max(...rows.map(row => Number(row.year || 0)), 0);
    return latest ? rows.filter(row => row.year >= latest - 49) : rows;
  }, [data]);
  const matrixOutcomes = useMemo(() => (data?.outcomes || [])
    .filter(item => item.code !== 'not_classified')
    .slice(0, 5), [data]);

  return (
    <Box sx={{ height: '100%', overflowY: 'auto', pb: 5 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
        <Stack direction="row" spacing={1.4} alignItems="center" sx={{ flex: 1 }}>
          <Box sx={{ width: 44, height: 44, borderRadius: 2.5, display: 'grid', placeItems: 'center', bgcolor: 'primary.main', color: 'primary.contrastText' }}><InsightsRounded /></Box>
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="h5" sx={{ fontWeight: 820, letterSpacing: '-.03em' }}>{embedded ? 'Pattern analysis' : 'Case research intelligence'}</Typography>
              <Chip size="small" label="Explainable · not predictive" color="success" variant="outlined" />
            </Stack>
            <Typography variant="body2" color="text.secondary">Use explainable classifications to discover case groups, then inspect the complete underlying decisions.</Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<LibraryAddRounded />} onClick={() => navigate('/research?view=decisions&import=1')}>Add cases</Button>
          <Button variant="contained" startIcon={<SearchRounded />} onClick={() => openArchive({})}>Browse all cases</Button>
        </Stack>
      </Stack>

      <Paper component="form" onSubmit={searchDecisions} variant="outlined" sx={{ mt: 2, p: { xs: 1.5, md: 2 }, borderRadius: 3, borderColor: 'primary.main', bgcolor: 'action.hover' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} alignItems={{ md: 'center' }}>
          <Box sx={{ minWidth: { md: 250 } }}>
            <Typography sx={{ fontWeight: 820 }}>Search the decisions</Typography>
            <Typography variant="caption" color="text.secondary">Enter a case, citation, issue, legal test, or fact pattern.</Typography>
          </Box>
          <TextField
            fullWidth
            size="small"
            value={draftQuery}
            onChange={event => setDraftQuery(event.target.value)}
            placeholder="e.g. murder intent intoxication, R v Jordan, Charter s. 11(b)"
            autoComplete="off"
            inputProps={{ 'aria-label': 'Search decisions from pattern analysis' }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchRounded fontSize="small" /></InputAdornment> }}
          />
          <Button type="submit" variant="contained" startIcon={<SearchRounded />} sx={{ whiteSpace: 'nowrap', minHeight: 40 }}>Search decisions</Button>
        </Stack>
        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1.25 }}>
          <Typography variant="caption" color="text.secondary">Try:</Typography>
          {['murder mens rea', 'Jordan unreasonable delay', 'self-defence reasonable grounds'].map(example => (
            <Chip key={example} size="small" label={example} variant="outlined" onClick={() => { setDraftQuery(example); setQuery(example); navigate(`/research?view=decisions&q=${encodeURIComponent(example)}`); }} />
          ))}
          <Typography variant="caption" color="text.secondary" sx={{ ml: { md: 'auto !important' } }}>Search opens the matching judgments; charts below describe the selected court and year scope.</Typography>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ mt: 2, borderRadius: 3, overflow: 'hidden' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontWeight: 820 }}>{query ? `Cases matching “${query}”` : 'Cases to start with'}</Typography>
            <Typography variant="caption" color="text.secondary">{query ? `${formatNumber(starterTotal)} matching decisions. Open one to read and verify the complete reasons.` : 'The newest available decisions. Open one, or search by issue, authority, citation, or facts above.'}</Typography>
          </Box>
          <Button endIcon={<ArrowForwardRounded />} onClick={() => openArchive({})}>{query ? 'View all matches' : 'Browse all cases'}</Button>
        </Stack>
        {starterLoading && <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2,minmax(0,1fr))' } }}>{Array.from({ length: 6 }, (_, index) => <Box key={index} sx={{ p: 1.75, borderBottom: '1px solid', borderColor: 'divider' }}><Skeleton width="70%" /><Skeleton width="42%" /><Skeleton width="85%" /></Box>)}</Box>}
        {!starterLoading && starterError && <Alert severity="error" sx={{ m: 1.5 }}>{starterError}</Alert>}
        {!starterLoading && !starterError && starterCases.length === 0 && <Box sx={{ px: 2, py: 3, textAlign: 'center' }}><Typography sx={{ fontWeight: 760 }}>No decisions match this starting point</Typography><Typography variant="body2" color="text.secondary">Try a broader phrase or browse all cases.</Typography><Button sx={{ mt: 1 }} onClick={showNewestDecisions}>Show newest decisions</Button></Box>}
        {!starterLoading && !starterError && starterCases.length > 0 && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2,minmax(0,1fr))' } }}>
            {starterCases.map((decision, index) => (
              <ButtonBase key={decision.id} onClick={() => openDecision(decision)} sx={{ display: 'block', width: '100%', textAlign: 'left', px: 2, py: 1.6, borderBottom: '1px solid', borderRight: { lg: index % 2 === 0 ? '1px solid' : 'none' }, borderColor: 'divider', '&:hover': { bgcolor: 'action.hover' } }}>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 800, lineHeight: 1.35 }}>{decision.name}</Typography>
                    <Typography sx={{ mt: 0.35, fontSize: 12.5, fontWeight: 760, color: 'primary.main' }}>{decision.citation || 'Neutral citation unavailable'}</Typography>
                  </Box>
                  <ArrowForwardRounded color="action" fontSize="small" />
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.55 }}>{decision.court} · {decision.decisionDate || decision.year || 'Date unavailable'}</Typography>
                <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                  {decision.analysis?.primaryCharge?.code && decision.analysis.primaryCharge.code !== 'not_classified' && <Chip size="small" variant="outlined" color="primary" label={decision.analysis.primaryCharge.label} />}
                  {decision.analysis?.outcome?.code && decision.analysis.outcome.code !== 'not_classified' && <Chip size="small" variant="outlined" label={decision.analysis.outcome.label} />}
                </Stack>
              </ButtonBase>
            ))}
          </Box>
        )}
      </Paper>

      <Alert severity="info" icon={<VerifiedUserOutlined />} sx={{ mt: 2, borderRadius: 2.5 }}>
        These are deterministic text classifications for research navigation—not legal advice, causal findings, “win rates,” or outcome predictions. A category can reflect an issue discussed rather than decided; verify the judgment, source, publication restrictions, and subsequent treatment.
      </Alert>

      {status && !status.available && <Alert severity="warning" sx={{ mt: 2 }}>{status.message || 'The supplied case archive is being prepared for hosted research.'}</Alert>}

      <Paper variant="outlined" sx={{ mt: 2, p: 1.5, borderRadius: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ sm: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 260 }}>
            <InputLabel>Court</InputLabel>
            <Select label="Court" value={court} onChange={event => setCourt(event.target.value)}>
              <MenuItem value="">All courts</MenuItem>
              {(status?.courts || []).map(item => <MenuItem key={item.code} value={item.code}>{item.name} ({formatNumber(item.cases)})</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label="From year" size="small" value={yearFrom} onChange={event => setYearFrom(event.target.value.replace(/\D/g, '').slice(0, 4))} sx={{ width: { sm: 125 } }} />
          <TextField label="To year" size="small" value={yearTo} onChange={event => setYearTo(event.target.value.replace(/\D/g, '').slice(0, 4))} sx={{ width: { sm: 125 } }} />
          <Button startIcon={<RestartAltRounded />} onClick={() => { setCourt(''); setYearFrom(''); setYearTo(''); }}>Reset scope</Button>
          <Typography variant="caption" color="text.secondary" sx={{ ml: { sm: 'auto !important' } }}>
            {data ? `${formatNumber(data.summary.cases)} decisions in current scope` : 'Loading scope…'}
          </Typography>
        </Stack>
      </Paper>

      {loading && <Stack alignItems="center" spacing={1.5} sx={{ py: 12 }}><CircularProgress /><Typography color="text.secondary">Computing research patterns…</Typography></Stack>}
      {!loading && error && <Alert severity="warning" sx={{ mt: 2 }}>{error}</Alert>}
      {!loading && !error && data && (
        <>
          <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(5, 1fr)' }, gap: 1.5 }}>
            <StatCard label="Decisions" value={data.summary.cases} detail="Analyzed in current scope" />
            <StatCard label="Charge identified" value={data.summary.charge_classified} detail={`${Math.round((data.summary.charge_classified / Math.max(1, data.summary.cases)) * 100)}% coverage`} tone="#3267A8" />
            <StatCard label="Outcome identified" value={data.summary.outcome_classified} detail={`${Math.round((data.summary.outcome_classified / Math.max(1, data.summary.cases)) * 100)}% coverage`} tone="#8A5DA6" />
            <StatCard label="Sentence signal" value={data.summary.sentences_detected} detail="Text-detected; verify manually" tone="#B77718" />
            <StatCard label="Publication flags" value={data.summary.publication_restrictions} detail="Review before disclosure" tone="#B34747" />
          </Box>

          <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' }, gap: 2 }}>
            <HorizontalBars title="Charge families" subtitle="Primary charge signal; select a bar to inspect the matching decisions." data={data.charges} color="#14877F" onOpen={item => openArchive({ charge: item.code })} />
            <HorizontalBars title="Recurring legal issues" subtitle="Issues can co-occur; select a bar to inspect the matching decisions." data={data.issues} color="#446B9E" onOpen={item => openArchive({ issue: item.code })} />
            <HorizontalBars title="Dispositions and outcomes" subtitle="Disposition language detected in the reasons; select a bar to inspect." data={data.outcomes} color="#8A5DA6" onOpen={item => openArchive({ outcome: item.code })} />
            <HorizontalBars title="Charter sections" subtitle="Charter provisions detected in the judgment text; sections can co-occur." data={data.charter} color="#B77718" onOpen={item => openArchive({ charter: item.code })} />
          </Box>

          <Paper variant="outlined" sx={{ mt: 2, p: 2.25, borderRadius: 3 }}>
            <Typography sx={{ fontWeight: 800 }}>Disposition patterns over the latest 50 archive years</Typography>
            <Typography variant="caption" color="text.secondary">Raw decision counts by judgment year; changing publication volume and court mix affect the trend.</Typography>
            <Box sx={{ mt: 2, height: 340 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trendData} margin={{ top: 8, right: 18, left: 8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.35} />
                  <XAxis dataKey="year" tick={{ fontSize: 11 }} minTickGap={28} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={formatNumber} />
                  <ChartTooltip formatter={value => formatNumber(value)} />
                  <Legend />
                  <Line type="monotone" dataKey="acquitted" name="Acquitted / dismissed" stroke="#14877F" dot={false} strokeWidth={2} />
                  <Line type="monotone" dataKey="convictions" name="Conviction / guilty plea" stroke="#8A5DA6" dot={false} strokeWidth={2} />
                  <Line type="monotone" dataKey="appeals_allowed" name="Appeal allowed / new trial" stroke="#D08A26" dot={false} strokeWidth={2} />
                  <Line type="monotone" dataKey="appeals_dismissed" name="Appeal dismissed" stroke="#55708C" dot={false} strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Paper>

          <Paper variant="outlined" sx={{ mt: 2, borderRadius: 3, overflow: 'hidden' }}>
            <Box sx={{ px: 2.25, py: 2 }}>
              <Typography sx={{ fontWeight: 800 }}>Charge × disposition research matrix</Typography>
              <Typography variant="caption" color="text.secondary">Descriptive shares within each text-classified charge family. Select a count to review the actual decisions.</Typography>
            </Box>
            <TableContainer>
              <Table size="small" sx={{ minWidth: 900 }}>
                <TableHead><TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell sx={{ fontWeight: 800 }}>Charge family</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800 }}>All</TableCell>
                  {matrixOutcomes.map(outcome => <TableCell key={outcome.code} align="right" sx={{ fontWeight: 800 }}>{outcome.label}</TableCell>)}
                  <TableCell align="right" sx={{ fontWeight: 800 }}>Research</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {data.chargeOutcome.map(row => (
                    <TableRow hover key={row.code}>
                      <TableCell sx={{ fontWeight: 720 }}>{row.label}</TableCell>
                      <TableCell align="right">{formatNumber(row.total)}</TableCell>
                      {matrixOutcomes.map(outcome => {
                        const count = row.outcomes[outcome.code]?.cases || 0;
                        return <TableCell key={outcome.code} align="right"><Button size="small" disabled={!count} onClick={() => openArchive({ charge: row.code, outcome: outcome.code })} sx={{ minWidth: 0, px: 0.5 }}><Typography component="span" sx={{ fontSize: 13, fontWeight: 700 }}>{formatNumber(count)}</Typography><Typography component="span" variant="caption" color="text.secondary"> · {Math.round((count / Math.max(1, row.total)) * 100)}%</Typography></Button></TableCell>;
                      })}
                      <TableCell align="right"><Button size="small" endIcon={<ArrowForwardRounded />} onClick={() => openArchive({ charge: row.code })}>Cases</Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.15fr) minmax(340px, .85fr)' }, gap: 2 }}>
            <Paper variant="outlined" sx={{ p: 2.25, borderRadius: 3 }}>
              <Typography sx={{ fontWeight: 800 }}>Detected custodial-term patterns</Typography>
              <Typography variant="caption" color="text.secondary">Only explicit, machine-detected terms. Concurrent sentences, credit, parole eligibility, ancillary orders, and offence-specific context are not normalized.</Typography>
              <TableContainer sx={{ mt: 1.25 }}><Table size="small"><TableHead><TableRow><TableCell>Charge</TableCell><TableCell align="right">Cases</TableCell><TableCell align="right">Average</TableCell><TableCell align="right">Range</TableCell></TableRow></TableHead><TableBody>
                {data.sentencePatterns.slice(0, 10).map(row => <TableRow hover key={row.code} onClick={() => openArchive({ charge: row.code })} sx={{ cursor: 'pointer' }}><TableCell>{row.label}</TableCell><TableCell align="right">{formatNumber(row.cases)}</TableCell><TableCell align="right">{row.averageMonths} mo.</TableCell><TableCell align="right">{row.minimumMonths}–{row.maximumMonths} mo.</TableCell></TableRow>)}
              </TableBody></Table></TableContainer>
            </Paper>
            <Stack spacing={2}>
              <HorizontalBars title="Procedural stage" subtitle="Court level and decision-language classification." data={data.stages} color="#8A5DA6" onOpen={item => openArchive({ stage: item.code })} />
              <Paper variant="outlined" sx={{ p: 2.25, borderRadius: 3 }}>
                <Stack direction="row" spacing={1.25} alignItems="center"><GavelRounded color="primary" /><Box><Typography sx={{ fontWeight: 800 }}>Research protocol</Typography><Typography variant="caption" color="text.secondary">Treat patterns as a discovery layer.</Typography></Box></Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, lineHeight: 1.65 }}>Open the underlying cases, confirm the actual charge and disposition, review the complete reasons, check subsequent treatment, and validate quotations against the authoritative source before use.</Typography>
                <Button fullWidth variant="outlined" sx={{ mt: 1.5 }} onClick={() => openArchive({})}>Open filtered archive</Button>
              </Paper>
            </Stack>
          </Box>
        </>
      )}
    </Box>
  );
}
